import { getStateFromPath } from '@react-navigation/native';
import { useCallback } from 'react';
import { DeepLinkType, parseDeepLink, } from 'common/NavigatorActions/hooks/parseDeepLink';
import useEnergyCompare, { mapMerchantTypeToCompareType, } from 'features/feed/components/save/useEnergyCompare';
import { getPots, getPotsAccount } from 'features/pots/actions';
import { useNavigateToAutosave } from 'features/pots/hooks/useNavigateToAutosave';
import useGetCreditReport from 'features/credit-score/hooks/useGetCreditReport';
import { selectCreditReportAccountById, selectCreditReportSearchByReference, selectFullCreditAlertsUnlocked, selectIsCreditScoreAlertsAvailable, selectIsSoftCreditReportSearch, } from 'features/credit-score/selectors';
import { benefits } from 'features/premium/benefits';
import { selectUserTier } from 'features/premium/selectors/tier';
import useOpenRefundFlow from 'features/premium/hooks/useOpenRefundFlow';
import { isUnlocked } from 'utils';
import useMonevoEntry from 'features/borrow/monevo/hooks/useMonevoEntry';
import { selectDidViewFraudDetectionIntro } from 'features/quests/store/selectors';
import { selectHasCurrentRentReporting } from 'features/rent-reporting/selectors';
import { setSelectedSpace } from 'features/spaces/actions';
import { parse } from 'query-string';
import Modal from 'utils/packages/Modal';
import { selectAccountById, selectCategory, selectNavigatorConfig, selectUser, selectHasSetBudget, selectIsGBUser, } from 'reducers/selectors';
import { selectMonthlyCategories, selectMonthlyMerchants, selectMonthlyTotalsWithBudgets, selectPosition, } from 'features/budgeting/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import Links from 'utils/links';
import { Tier } from 'features/premium/entities';
import { MERCHANT_SUGGESTION_TYPES } from 'features/transactions/types';
import { OnboardingFlow } from 'utils/types/navigation';
import { useLinkTo, useMainStackNavigation } from 'utils/types/navigationV6';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import { getExtraMemberInviteLinkInfo } from 'actions/sharedPlans';
import { getAlpacaAccount } from 'features/invest/utils';
import { getTradingAccounts, getConnectedAccount, setSelectedTradingAccountId, getOrder, } from 'features/invest/actions';
import { makeSelectSubscriptionById } from 'features/subscriptions/selectors';
import { setBudgetingPosition } from 'actions';
import { findMerchantInMostRecentPeriod } from 'features/budgeting/utils';
import { selectPots } from 'features/pots/reducers/selectors';
import { ChartType } from 'features/analytics/types';
import { selectTradingAccountFromId } from 'features/invest/reducers/selectors';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import useNavigateToNextScreen from 'features/invest/hooks/useNavigateToNextScreen';
import { isDesktop } from '../constants';
import { useAddAccountModal } from '../features/accounts/hooks/useAddAccountModal';
import { makeSelectTransactionById } from '../features/transactions/selectors';
const deserializeBool = (s) => s === 'true';
const deserializeInt = (s) => parseInt(s, 10);
const deserializeEnum = (enums) => (s) => enums.includes(s) ? s : undefined;
export const parseId = { id: deserializeInt };
const getTier = (tier) => {
    if (tier === Tier.pro || tier === Tier.ultimate || tier === Tier.plus) {
        return tier;
    }
    return undefined;
};
export const linking = {
    prefixes: [],
    config: {
        screens: {
            Feed: 'feed',
            Save: 'save',
            InvestDiscover: 'invest/discover',
            Reward: 'cashback/:id',
            Rewards: 'cashback',
            ClaimRewards: 'cashback-claim',
            Notifications: 'notifications/settings',
            Borrow: 'borrow',
            EmmaProModal: {
                path: 'upgrade/:skipTrialScreen/:tier?',
                parse: {
                    skipTrialScreen: deserializeBool,
                    currentAndAbovePlan: deserializeBool,
                },
            },
            ManagePremiumSubscription: 'manage-plan',
            ChooseBank: {
                path: 'chooseBank/:type/:back?',
                parse: {
                    type: (type) => type === 'personal' || type === 'business',
                },
            },
            InvestStock: {
                path: 'stocks/:symbolOrAssetId',
                parse: {
                    symbolOrAssetId: (symbolOrAssetId) => symbolOrAssetId,
                },
            },
            PensionBee: 'pensionbee',
            NetWorth: 'net-worth',
            AccountsOverview: 'accounts',
            MySpaces: 'spaces',
            Logins: 'logins',
            BankConnection: {
                path: 'bank-connections/:connectionId',
                parse: { connectionId: deserializeInt },
            },
            ClosedAccounts: {
                path: 'bank-connections/:connectionId/closed-accounts',
                parse: { connectionId: deserializeInt },
            },
            BrokenConnections: 'broken-connections',
            EditCategory: 'custom-categories',
            DataProtectionScreen: 'data-protection-screen',
            SmartRules: 'automation-rules',
            SmartRuleIntro: 'automation-rules/intro',
            SmartRuleName: 'automation-rules/name',
            Support: 'support',
            InviteScreen: 'invite',
            ReferralPayout: {
                path: 'referral-payout',
                parse: {
                    creditReferrals: deserializeBool,
                },
            },
            Subscriptions: 'subscriptions',
            Analytics: {
                path: 'analytics',
                parse: {
                    chartType: (s) => {
                        switch (s) {
                            case 'income_spending':
                                return ChartType.income_spending;
                            case 'over_time':
                                return ChartType.over_time;
                            case 'categories':
                                return ChartType.categories;
                            default:
                                return undefined;
                        }
                    },
                },
            },
            Budgeting: 'budgeting',
            SetUpBudget: 'budgeting-setup',
            EditBudget: 'budgeting/:flow', // /budgeting/add, budgeting/edit
            ExportData: 'export-data',
            ExportDataLive: 'export-data-live',
            ExportDataLiveTransit: {
                path: 'export-data-live/transit',
                parse: {
                    manageIfEnabled: deserializeBool,
                    enableIfAuthenticated: deserializeBool,
                },
            },
            ExportDataLiveRetry: 'export-data-live/retry',
            OfflineAccounts: 'offline-accounts',
            Transactions: {
                path: 'accounts/:id', // will need to check space and switch,
                parse: parseId,
            },
            ActivityScreen: 'pay/activity',
            EditSpace: {
                path: 'spaces/:id/edit',
                parse: parseId,
            },
            GroupJoin: 'join-group',
            Group: {
                path: 'groups/:id',
                parse: parseId,
            },
            JoinSpace: 'join-space/:accessCode',
            JoinedSpace: 'joined-space',
            ModalPaymentRequest: 'payment-request',
            AutoSave: 'auto-save',
            AutosaveIntro: 'auto-save/intro',
            AutosaveRoundupSettings: 'auto-save/roundup-settings',
            AutosaveDistribution: {
                path: 'auto-save/distribution',
                parse: {
                    isEditFlow: deserializeBool,
                },
            },
            PotList: 'pots',
            SelectPotType: 'create-pot',
            TransactionsList: 'transactions',
            ScheduledTransactionList: 'scheduled-transactions',
            NotificationsList: 'notifications',
            DailyBalanceNotifications: 'notifications/daily-balance',
            CommittedList: 'committed-spending',
            Item: {
                path: 'transactions/:id',
                parse: parseId,
            },
            Category: 'analytics-category',
            MonthlyCategory: 'budgeting-category',
            InactivePayments: {
                path: 'subscriptions/inactive',
            },
            Subscription: {
                path: 'subscriptions/:id',
                parse: parseId,
            },
            AccountSettings: 'settings',
            AccountSettingsList: 'settings/account-details',
            PreferenceSettingsList: 'settings/preferences',
            AboutUs: 'settings/about-us',
            ImportantStuff: 'settings/important-stuff',
            PersonalDetails: 'settings/personal-details',
            ChangePasscode: 'settings/change-passcode',
            PlanOverview: 'settings/plan-overview',
            Quests: 'quests',
            Learn: 'learn',
            SuggestMerchantOptions: {
                path: 'transactions/merchant-suggestions/options',
                parse: {
                    transactionId: deserializeInt,
                },
            },
            MerchantFeedback: {
                path: 'transactions/merchant-suggestions/find',
                parse: {
                    transactionId: deserializeInt,
                    suggestType: deserializeEnum(MERCHANT_SUGGESTION_TYPES),
                },
            },
            ConfirmMerchant: {
                path: 'transactions/merchant-suggestions/confirm-merchant',
                parse: {
                    transactionId: deserializeInt,
                },
            },
            PreviewMerchantOverride: {
                path: 'transactions/merchant-suggestions/preview',
                parse: {
                    transactionId: deserializeInt,
                },
            },
            SearchTransactions: 'search-transactions',
            EditAccountName: {
                path: 'accounts/:id/rename',
                parse: parseId,
            },
            EditOverdraft: {
                path: 'accounts/:id/overdraft',
                parse: parseId,
            },
            SavingGoalEdit: {
                path: 'saving-goals/edit',
                parse: parseId,
            },
            AnalyticsIntro: 'analytics/intro',
            SelectMerchant: 'budgeting-merchant/select',
            EditMerchantBudgets: 'budgeting-merchant',
            EditMerchantBudget: {
                path: 'budgeting-merchant/edit',
                parse: {
                    transactionsCount: deserializeInt,
                    id: deserializeInt,
                    backCount: deserializeInt,
                },
            },
            UnbudgetedSpend: 'unbudgeted-spending',
            DummyAccountSetup: {
                path: 'offline-accounts/setup',
                parse: parseId,
            },
            DummyAccountCreated: {
                path: 'offline-accounts/created',
                parse: parseId,
            },
            DummyAccountLogo: {
                path: 'offline-accounts/logo',
                parse: parseId,
            },
            DummyAccountLogoType: {
                path: 'offline-accounts/logo-type',
                parse: parseId,
            },
            DummyAccountCreationImport: {
                path: 'offline-accounts/import',
                parse: parseId,
            },
            HasValuationChanged: {
                path: 'offline-accounts/has-valuation-changed',
                parse: parseId,
            },
            DummyAccountImportTransactions: {
                path: 'offline-accounts/import-transactions',
                parse: parseId,
            },
            DummyAccountImportAddTransactions: {
                path: 'offline-accounts/import-add-transactions',
                parse: parseId,
            },
            DummyAccountImportCurrency: {
                path: 'offline-accounts/import-currency',
                parse: parseId,
            },
            AddBalance: {
                path: 'offline-accounts/balance',
                parse: parseId,
            },
            AddAmount: {
                path: 'offline-accounts/add-amount',
            },
            AddValuation: {
                path: 'offline-accounts/add-valuation',
            },
            AddAnEmoji: {
                path: 'offline-accounts/emoji',
                parse: parseId,
            },
            PickAccountType: 'offline-accounts/type',
            Property: 'offline-accounts/property-type',
            // MySpaces and EditSpace already exist
            CreateSpaceName: 'spaces/create/name',
            EditSpaceAccounts: 'spaces/:id/edit/accounts',
            CreateSpaceAccounts: 'spaces/create/accounts',
            CreateSpaceFinished: 'spaces/create/finished',
            PrimarySpaceAccounts: 'spaces/primary/accounts',
            AuthComplete: 'auth-complete',
            Transfer: 'transfer',
            ManageNordVpn: 'nordvpn',
        },
    },
    getStateFromPath: (path, options) => {
        const state = getStateFromPath(path, options);
        if (path.startsWith('/cashback?id=')) {
            const params = state?.routes[0]?.params;
            if ('id' in params) {
                const { id } = params;
                return getStateFromPath(`/cashback/${id}`, options);
            }
        }
        if (path === '/upgrade' || path.startsWith('/upgrade/')) {
            const params = state?.routes[0]
                ?.params;
            const skipTrialScreen = params && 'skipTrialScreen' in params
                ? !!params.skipTrialScreen
                : false;
            let outPath = `/upgrade/${skipTrialScreen}`;
            const tier = getTier(params?.tier);
            if (tier) {
                outPath += `/${tier}`;
            }
            const queryIndex = path.indexOf('?');
            if (queryIndex >= 0) {
                outPath += path.substring(queryIndex);
            }
            return getStateFromPath(outPath, options);
        }
        if (path.startsWith('/budgeting/')) {
            const params = state?.routes[0]?.params;
            const flow = params.flow === 'edit' ? 'Edit' : 'Set';
            return getStateFromPath(`/budgeting/${flow}`, options);
        }
        if (path.startsWith('/analytics-category') && state?.routes[0]) {
            return {
                ...state,
                routes: [
                    {
                        ...state.routes[0],
                        params: {
                            runOnMount: true,
                            step: 'custom',
                            ...state.routes[0].params,
                        },
                    },
                ],
            };
        }
        return state;
    },
};
const abstractRoutes = ['/upgrade-to-paid', '/connections/fix'];
export const isValidAppLink = (rawRoute) => {
    const route = rawRoute.replace('/dashboard', '');
    if (abstractRoutes.includes(route)) {
        return true;
    }
    if (route.startsWith('/@')) {
        return true;
    }
    if (route.startsWith('/auto-invest/')) {
        return true;
    }
    if (/^\/credit-score([/?#]|$)/.test(route)) {
        return true;
    }
    // Handled in appLinkTo, not in linking.config
    if (/^\/invest([/?#]|$)/.test(route)) {
        return true;
    }
    const result = getStateFromPath(route, {
        ...linking.config,
        screens: {
            ...linking.config?.screens,
            // These routes aren't part of the main config, we have custom handling for them
            RentReporting: 'rent-reporting',
            DataProtectionScreen: 'fraud-detection',
            BroadBandQuotes: 'broadband',
            EnergyQuotes: 'energy-quotes',
            UserInfo: 'loan',
            InvestAccount: 'invest/account',
            InvestSelectDocument: 'wallet/start-poi',
            InvestChooseProofOfAddress: 'wallet/start-poa',
            AddAccountModal: 'add-account',
            EditPotGoal: 'pots/:id/setup-goal',
            ManagePremiumSubscriptionRefund: 'manage-plan/refund',
        },
    });
    return result !== undefined;
};
const useAppLink = () => {
    const linkTo = useLinkTo();
    const navigation = useMainStackNavigation();
    const { navigateToTab } = useTabNavigation();
    const navigateToAutosave = useNavigateToAutosave();
    const navigateForInvestOnboarding = useNavigateToNextScreen();
    const navigateForPotsOnboarding = useNavigateToNextScreenForPotsOnboarding();
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const onAddAccount = useAddAccountModal('Net worth');
    const openRefundFlow = useOpenRefundFlow();
    const hasSetBudget = useAppSelector(selectHasSetBudget);
    const fetchCreditReport = useGetCreditReport();
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
    }, []);
    const energyCompare = useEnergyCompare(onCompare);
    const openMonevo = useMonevoEntry();
    const appLinkTo = useCallback(async (rawRoute) => {
        if (rawRoute) {
            const isGBUser = selectIsGBUser(store.getState());
            const route = rawRoute.replace('/dashboard', '');
            if (route.startsWith('/invest/setup')) {
                navigateForInvestOnboarding();
                return;
            }
            // /auto-invest/${autoInvestPlanId}?accountId=${accountId}
            if (route.startsWith('/auto-invest/')) {
                if (isGBUser) {
                    const queryParams = route.split('?');
                    const query = parse(queryParams[1]);
                    const accountId = !Array.isArray(query.accountId)
                        ? query.accountId
                        : undefined;
                    if (accountId) {
                        const tradingAccount = selectTradingAccountFromId(store.getState(), accountId);
                        if (tradingAccount?.status === 'ACTIVE' &&
                            (tradingAccount.accountType === 'ISA' ||
                                tradingAccount.accountType === 'GIA')) {
                            dispatch(setSelectedTradingAccountId(accountId));
                            navigation.navigate('AutoinvestManage', {
                                accountType: tradingAccount.accountType,
                            });
                            return;
                        }
                    }
                    navigateToTab('Invest');
                }
                return;
            }
            if (route.startsWith('/credit-score')) {
                // If notification comes through but shouldn't or user is not on required tier
                if (!selectIsCreditScoreAlertsAvailable(store.getState()) ||
                    !selectFullCreditAlertsUnlocked(store.getState())) {
                    navigateToTab('Borrow');
                    return;
                }
                const pathOnly = route.split('?')[0];
                const segments = pathOnly.split('/').filter(Boolean);
                if (segments[1] === 'alerts') {
                    navigation.navigate('CreditScoreAlertsScreen');
                    return;
                }
                if (segments.length < 3 || segments[1] !== 'report') {
                    navigateToTab('Borrow');
                    return;
                }
                const type = segments[2];
                const id = segments[3];
                const hasFullCreditReport = isUnlocked(selectUserTier(store.getState()), benefits.creditScoreReports.minTier);
                if (!hasFullCreditReport) {
                    navigateToTab('Borrow');
                    return;
                }
                switch (type) {
                    case 'accounts': {
                        const accountId = id ? Number(id) : NaN;
                        if (!Number.isFinite(accountId)) {
                            navigation.navigate('CreditReportAccountsScreen');
                            return;
                        }
                        let account = selectCreditReportAccountById(store.getState(), accountId);
                        if (!account) {
                            await dispatch(fetchCreditReport());
                            account = selectCreditReportAccountById(store.getState(), accountId);
                        }
                        if (account) {
                            navigation.navigate('CreditReportAccountDetailScreen', {
                                accountId,
                            });
                        }
                        else {
                            navigation.navigate('CreditReportAccountsScreen');
                        }
                        return;
                    }
                    case 'searches': {
                        if (!id) {
                            navigation.navigate('CreditReportSearchScreen', {
                                isSoft: false,
                            });
                            return;
                        }
                        let search = selectCreditReportSearchByReference(store.getState(), id);
                        if (!search) {
                            await dispatch(fetchCreditReport());
                            search = selectCreditReportSearchByReference(store.getState(), id);
                        }
                        if (search) {
                            navigation.navigate('CreditReportSearchDetailScreen', {
                                searchReference: id,
                            });
                        }
                        else {
                            navigation.navigate('CreditReportSearchScreen', {
                                isSoft: selectIsSoftCreditReportSearch(store.getState(), id),
                            });
                        }
                        return;
                    }
                    case 'judgments':
                        navigation.navigate('CreditReportRulingDetailScreen', {
                            type: 'judgment',
                            scrollTarget: id,
                        });
                        return;
                    case 'insolvencies':
                        navigation.navigate('CreditReportRulingDetailScreen', {
                            type: 'insolvency',
                            scrollTarget: id,
                        });
                        return;
                    case 'cifasCases':
                        navigation.navigate('CreditReportRulingDetailScreen', {
                            type: 'cfasCases',
                            scrollTarget: id,
                        });
                        return;
                    case 'addressLinks':
                        navigation.navigate('CreditReportIdentityDetailScreen', {
                            type: 'address',
                            scrollTarget: id,
                        });
                        return;
                    case 'financialConnections':
                        navigation.navigate('CreditReportIdentityDetailScreen', {
                            type: 'financialConnections',
                            scrollTarget: id,
                        });
                        return;
                    case 'otherNames':
                        navigation.navigate('CreditReportIdentityDetailScreen', {
                            type: 'otherNames',
                            scrollTarget: id,
                        });
                        return;
                    case 'electoralRoll':
                    case 'personalInformation':
                        navigation.navigate('CreditReportIdentityDetailScreen', {
                            type: 'personal',
                            scrollTarget: id,
                        });
                        return;
                    default:
                        navigation.navigate('CreditReportAccountsScreen');
                        return;
                }
            }
            if (route.startsWith('/pots/') && route.includes('/setup-goal')) {
                const routeState = getStateFromPath(route, {
                    screens: { EditPotGoal: 'pots/:id/setup-goal' },
                });
                const params = routeState?.routes[0]?.params;
                await dispatch(getPots());
                const pots = selectPots(store.getState());
                const pot = pots.find((p) => p.id === params?.id);
                if (pot) {
                    navigation.navigate('EditPotGoal', { pot });
                }
                else {
                    navigation.navigate('PotList');
                }
                return;
            }
            if (route.startsWith('/link/extra-members/invites/')) {
                const code = route.split('/link/extra-members/invites/')[1];
                const res = await dispatch(getExtraMemberInviteLinkInfo(code));
                const { invites } = store.getState().sharedPlans;
                if (res?.type !== 'GET_EXTRA_MEMBER_INVITE_LINK_INFO_SUCCESS') {
                    const r = res;
                    if (r.apiPayload?.status === 400 &&
                        r.errorMessage ===
                            'Only users on free plan can access this feature') {
                        Modal.showError('You’re already on an Emma paid plan, so you can’t join this one.', undefined, 'You’re already on a paid plan', 'Okay, thanks');
                        return;
                    }
                }
                if (invites.length) {
                    navigation.navigate('ExtraMembersInvite');
                }
                else {
                    Modal.showError('This invite has already been used or has expired. Please ask the person who invited you for a new one.', undefined, 'Invite no longer active', 'Okay, thanks');
                    return;
                }
                return;
            }
            if (route === '/wallet/start-poi') {
                await dispatch(getPotsAccount());
                const requiredUserAction = store.getState().pots.account?.kycVerification?.requiredUserAction;
                if (requiredUserAction === 'UPLOAD_IDENTITY_DOCUMENT' && !isDesktop) {
                    navigation.navigateDeprecated('WealthDocumentUploadIntroScreen', {
                        documentType: 'POI',
                        flow: OnboardingFlow.Wallet,
                    });
                }
            }
            if (route === '/wallet/start-poa') {
                await dispatch(getPotsAccount());
                const requiredUserAction = store.getState().pots.account?.kycVerification?.requiredUserAction;
                if (requiredUserAction === 'UPLOAD_PROOF_OF_ADDRESS' && !isDesktop) {
                    navigation.navigateDeprecated('WealthDocumentUploadIntroScreen', {
                        documentType: 'POA',
                        flow: OnboardingFlow.Wallet,
                    });
                }
            }
            if (route === '/connections/fix') {
                navigation.navigate('BrokenConnections');
            }
            if (route.startsWith('/invest/account')) {
                const investAccount = getAlpacaAccount(store.getState().invest.accounts);
                if (investAccount?.account.status === 'ACTIVE') {
                    dispatch(setSelectedTradingAccountId(investAccount.account.id));
                    navigation.navigateDeprecated('InvestAccount');
                }
                else {
                    navigateToTab('Invest');
                }
                return;
            }
            if (route.startsWith('/auto-save/intro')) {
                await dispatch(getPotsAccount());
                const potsAccStatus = store.getState().pots.account?.status;
                if (potsAccStatus === 'NOT_STARTED') {
                    navigateToTab('Save', { tab: 'pots' });
                    navigateForPotsOnboarding();
                }
                else {
                    navigateToAutosave();
                }
                return;
            }
            if (route.startsWith('/auto-save/roundup-settings')) {
                await dispatch(getPotsAccount());
                const potsAccStatus = store.getState().pots.account?.status;
                if (potsAccStatus === 'NOT_STARTED') {
                    navigateToTab('Save', { tab: 'pots' });
                    navigateForPotsOnboarding();
                }
                else if (potsAccStatus === 'ACTIVE') {
                    const potsAutoSavePlan = store.getState().pots.savingsPlan;
                    if (potsAutoSavePlan) {
                        navigation.navigate('AutosaveRoundupSettings');
                    }
                    else {
                        navigation.navigate('AutosaveIntro');
                    }
                }
                else {
                    navigateToAutosave();
                }
                return;
            }
            if (route.startsWith('/invest')) {
                if (isGBUser) {
                    await dispatch(getTradingAccounts()); // doesnt throw
                    dispatch(getConnectedAccount());
                    dispatch(getPotsAccount());
                    const queryParams = route.split('?');
                    const query = parse(queryParams[1]);
                    const tradingAccountId = query?.tradingAccountId;
                    if (typeof tradingAccountId === 'string' &&
                        tradingAccountId.length) {
                        // case for activity or order
                        if (route.startsWith('/invest/activity')) {
                            const tradingAccounts = store.getState().invest.accounts;
                            const tradingAccount = tradingAccounts?.find((account) => account.account.id === tradingAccountId);
                            // there will be no alpaca notifications so excluding
                            if (tradingAccount &&
                                tradingAccount.account.broker === 'seccl') {
                                const orderId = query?.orderId;
                                if (typeof orderId === 'string' && orderId.length) {
                                    const orderPayload = await dispatch(getOrder(orderId, tradingAccountId));
                                    if (orderPayload?.payload?.order) {
                                        const { order } = orderPayload.payload;
                                        if (order.operation === 'switch') {
                                            navigation.navigate('InvestSwitchOrder', {
                                                id: order.id,
                                                status: order.status,
                                                assetId: order.assetId,
                                                switchOrderId: order.switchOrderId,
                                                switchAssetId: order.switchAssetId,
                                            });
                                            return;
                                        }
                                        if (order.status === 'completed') {
                                            if (order.filled?.amount && order.filled.quantity) {
                                                navigation.navigate('InvestReceipt', {
                                                    activity: {
                                                        id: order.id,
                                                        timestamp: order.timestamp,
                                                        amount: order.filled?.amount,
                                                        type: order.operation,
                                                        assetId: order.assetId,
                                                    },
                                                });
                                            }
                                            return;
                                        }
                                        navigation.navigate('InvestOrder', {
                                            order,
                                        });
                                        return;
                                    }
                                }
                                dispatch(setSelectedTradingAccountId(tradingAccount.account.id));
                                navigation.navigate('InvestNewActivityScreen');
                                return;
                            }
                        }
                        else {
                            // case for portfolio
                            const tradingAccounts = store.getState().invest.accounts;
                            const tradingAccount = tradingAccounts?.find((account) => account.account.id === tradingAccountId);
                            if (tradingAccount) {
                                dispatch(setSelectedTradingAccountId(tradingAccount.account.id));
                                navigation.navigate('InvestPortfolio');
                                return;
                            }
                        }
                    }
                    navigateToTab('Invest');
                }
                return;
            }
            if (route.startsWith('/upgrade-to-paid')) {
                linkTo('/upgrade/true');
                return;
            }
            if (route.startsWith('/fraud-detection')) {
                const didViewFraudDetectionIntro = selectDidViewFraudDetectionIntro(store.getState());
                if (didViewFraudDetectionIntro) {
                    navigation.navigateDeprecated('DataProtectionScreen');
                }
                else {
                    navigation.navigateDeprecated('FraudProtectionIntro');
                }
                return;
            }
            if (route.startsWith('/rent-reporting')) {
                const hasRentReporting = selectHasCurrentRentReporting(store.getState());
                if (hasRentReporting) {
                    navigation.navigateDeprecated('RentReporting');
                }
                else {
                    navigation.navigateDeprecated('RentReportingOnboarding');
                }
                return;
            }
            if (route.startsWith('/loan')) {
                if (route.includes('type=debt')) {
                    openMonevo('DEBT');
                    return;
                }
                if (route.includes('type=car-finance')) {
                    openMonevo('CAR_FINANCE');
                    return;
                }
                if (route.includes('type=home-improvements')) {
                    openMonevo('HOME_IMPROVEMENTS');
                    return;
                }
                if (route.includes('type=credit-cards')) {
                    openMonevo('CREDIT_CARD');
                    return;
                }
                openMonevo('LOAN');
                return;
            }
            if (route.startsWith('/broadband')) {
                energyCompare('Broadband');
                return;
            }
            if (route.startsWith('/energy-quotes')) {
                const params = getStateFromPath(route)?.routes[0]?.params;
                if (params?.type) {
                    energyCompare(mapMerchantTypeToCompareType(params.type));
                }
                return;
            }
            if (route.startsWith('/@')) {
                const routeState = getStateFromPath(route);
                if (!routeState)
                    return;
                const handle = routeState.routes[0]?.name?.replace('@', '');
                const params = routeState.routes[0]?.params;
                const payeeAccountId = params?.accountId
                    ? Number(params.accountId)
                    : undefined;
                const state = store.getState();
                const user = selectUser(state);
                if (handle) {
                    if (user.urlHandle.toLowerCase() === handle.toLowerCase()) {
                        if (selectNavigatorConfig(state).showPayTab) {
                            navigateToTab('Pay');
                        }
                        else {
                            navigation.navigate('Feed');
                        }
                        Modal.showError('You can’t request money from yourself; share this link to get paid in Emma', () => { }, 'Hold on 💫', 'Okay');
                    }
                    else {
                        navigation.navigateDeprecated('PayEnterAmount', {
                            to: {
                                payeeAccountId,
                                itemType: 'pay',
                                contact: { handle, phoneNumbers: [] },
                            },
                        });
                    }
                }
            }
            const split = route.split('/')[2];
            if (route.startsWith('/accounts') && split) {
                const state = store.getState();
                const accountFromId = selectAccountById(state, Number(split));
                if (!accountFromId) {
                    linkTo('/accounts');
                    return;
                }
                if (accountFromId.spaceId !== state.spaces.selectedSpace) {
                    // If the account is not in the current space then don’t navigate to it as it won’t work yet
                    return;
                }
            }
            if (route.startsWith('/feed')) {
                const queryParams = route.split('?');
                const query = parse(queryParams[1]);
                if (query && query.spaceId && !Array.isArray(query.spaceId)) {
                    dispatch(setSelectedSpace(Number(query.spaceId)));
                }
            }
            if (route.startsWith('/stocks/')) {
                const queryParams = route.split('?');
                const query = parse(queryParams[1]);
                if (query &&
                    query.tradingAccountId &&
                    !Array.isArray(query.tradingAccountId)) {
                    const { tradingAccountId } = query;
                    if (tradingAccountId) {
                        const tradingAccounts = store.getState().invest.accounts;
                        const tradingAccount = tradingAccounts?.find((account) => account.account.id === tradingAccountId);
                        if (tradingAccount) {
                            dispatch(setSelectedTradingAccountId(tradingAccount.account.id));
                        }
                        else {
                            // we donot want to continue as there is no matching trading account for the given trading account id
                            return;
                        }
                    }
                }
            }
            if (route.startsWith('/transfer')) {
                const queryParams = route.split('?');
                const query = parse(queryParams[1]);
                if (query && query.to && !Array.isArray(query.to)) {
                    const state = store.getState();
                    const toAccount = selectAccountById(state, Number(query.to));
                    if (toAccount) {
                        navigation.navigate('Transfer', {
                            toAccountId: Number(query.to),
                        });
                        return;
                    }
                }
                navigation.navigate('Transfer');
                return;
            }
            if (route.startsWith('/subscriptions') && split) {
                const subscription = makeSelectSubscriptionById()(store.getState(), Number(split));
                if (subscription) {
                    navigation.navigate('Subscription', { id: Number(split) });
                    return;
                }
                linkTo('/subscriptions');
                return;
            }
            if (route.startsWith('/budgeting')) {
                if (!hasSetBudget) {
                    linkTo('/budgeting-setup');
                    return;
                }
                if (route === '/budgeting-setup') {
                    linkTo('/budgeting');
                    return;
                }
            }
            if (route.startsWith('/budgeting-category')) {
                const queryParams = route.split('?');
                const query = parse(queryParams[1]);
                const state = store.getState();
                // Check if required params exist
                const hasCategory = query && 'categoryId' in query && query.categoryId;
                const hasMerchant = query && 'merchantId' in query && query.merchantId;
                const hasFromTo = query.from &&
                    query.to &&
                    !Array.isArray(query.from) &&
                    !Array.isArray(query.to);
                if (!hasFromTo && hasMerchant && hasSetBudget) {
                    const monthlyTotalsWithBudgets = selectMonthlyTotalsWithBudgets(state);
                    const monthlyMerchants = selectMonthlyMerchants(state);
                    const merchant = findMerchantInMostRecentPeriod(monthlyMerchants, monthlyTotalsWithBudgets, Number(query.merchantId));
                    if (merchant) {
                        if (merchant.baseLimit === null) {
                            navigation.navigate('EditMerchantBudget', {
                                displayName: merchant.displayName,
                                id: merchant.id,
                                iconUrl: merchant.iconUrl,
                            });
                            return;
                        }
                        navigation.navigate('MonthlyCategory', {
                            merchantId: merchant.id,
                            from: monthlyTotalsWithBudgets[0].from,
                            to: monthlyTotalsWithBudgets[0].to,
                        });
                        return;
                    }
                }
                if (hasCategory === hasMerchant || !hasFromTo) {
                    appLinkTo('/budgeting');
                    return;
                }
                // Build period key with proper type
                const periodKey = `${query.from}|${query.to}`;
                let dataExists = false;
                if (hasCategory) {
                    // Check if category exists in user's categories
                    const category = selectCategory(state, query.categoryId);
                    if (!category) {
                        appLinkTo('/budgeting');
                        return;
                    }
                    // Check if data exists for this category in the specified period
                    const monthlyCategories = selectMonthlyCategories(state);
                    const categoriesForPeriod = monthlyCategories[periodKey];
                    if (categoriesForPeriod?.categories) {
                        dataExists = categoriesForPeriod.categories.some((cat) => cat.id === query.categoryId);
                    }
                }
                if (hasMerchant) {
                    // Check if data exists for this merchant in the specified period
                    const monthlyMerchants = selectMonthlyMerchants(state);
                    const merchantsForPeriod = monthlyMerchants[periodKey];
                    if (merchantsForPeriod?.merchants) {
                        dataExists = merchantsForPeriod.merchants.some((merchant) => merchant.id === Number(query.merchantId));
                    }
                }
                if (dataExists) {
                    const monthlyTotalsWithBudgets = selectMonthlyTotalsWithBudgets(state);
                    const currentPosition = selectPosition(state);
                    const pos = monthlyTotalsWithBudgets.findIndex(({ from, to }) => from === query.from && to === query.to);
                    if (pos === -1) {
                        appLinkTo('/budgeting');
                        return;
                    }
                    dispatch(setBudgetingPosition(pos));
                    navigation.navigate('MonthlyCategory', {
                        merchantId: hasMerchant ? Number(query.merchantId) : undefined,
                        categoryId: hasCategory
                            ? query.categoryId
                            : undefined,
                        from: query.from,
                        to: query.to,
                        runOnMount: true,
                        resetPosition: currentPosition,
                    });
                }
                else {
                    appLinkTo('/budgeting');
                }
                return;
            }
            if (route.startsWith('/manage-plan/refund')) {
                await openRefundFlow();
                return;
            }
            if (route.startsWith('/add-account')) {
                onAddAccount();
                return;
            }
            if (route.startsWith('/transactions') && split) {
                const transactionFromId = makeSelectTransactionById()(store.getState(), Number(split));
                if (transactionFromId) {
                    navigation.navigate('Item', { id: Number(split) });
                    return;
                }
                linkTo('/transactions');
                return;
            }
            // Tab deep links must go through navigateToTab, which re-enables the
            // tab if the user removed it. Raw linkTo dispatches a NAVIGATE to an
            // unmounted tab, which goes unhandled.
            const [routePath, routeQuery] = route.split('?');
            if (routePath === '/save' || routePath === '/save/') {
                const query = parse(routeQuery ?? '');
                const tab = !Array.isArray(query.tab) ? query.tab : undefined;
                if (tab === 'pots' || tab === 'cashback' || tab === 'opportunities') {
                    navigateToTab('Save', { tab });
                }
                else {
                    navigateToTab('Save');
                }
                return;
            }
            if (routePath === '/borrow' || routePath === '/borrow/') {
                navigateToTab('Borrow');
                return;
            }
            try {
                linkTo(route);
            }
            catch {
                //
            }
        }
    }, [
        navigateForInvestOnboarding,
        navigateToTab,
        navigateForPotsOnboarding,
        navigateToAutosave,
        linkTo,
        openMonevo,
        energyCompare,
        hasSetBudget,
        openRefundFlow,
        onAddAccount,
        fetchCreditReport,
    ]);
    /**
     * Given a FULL universal url navigates to the matching route
     * Examples:
     * Will resolve emma-app.com links example https://emma-app.com/dashboard/invite
     * If it’s not an app URL, opens in safari webview
     */
    const appLinkToFromUrl = useCallback(async (url, fallbackToSafari = true) => {
        const deepLinkData = parseDeepLink(url);
        if (deepLinkData.type === DeepLinkType.APP_LINK &&
            isValidAppLink(deepLinkData.routeStr)) {
            appLinkTo(deepLinkData.routeStr);
        }
        else if (fallbackToSafari) {
            Links.openLink(url);
        }
    }, [appLinkTo]);
    return { appLinkTo, appLinkToFromUrl, isValidAppLink };
};
export default useAppLink;
